import { useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { callRecordingsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useConfirm } from '../components/common/ConfirmDialog'

// Gesprächsaufnahme (docs/call-recording.md): Rechtshinweis bestätigen,
// externen Speicherort einrichten/testen, Aufnahmen anhören. Aufgenommen
// wird erst, wenn beides erfüllt ist ("Gate").

interface StorageInfo {
  storage_type: 'sftp' | 'webdav' | 's3'
  config: Record<string, string | number>
  has_secret: boolean
  sftp_public_key: string | null
  sftp_host_key_fingerprint: string | null
  verified_at: string | null
  last_test_at: string | null
  last_error: string | null
}

interface Status {
  allowed: boolean
  reasons: string[]
  consent: { confirmed: boolean; confirmed_by: string | null; confirmed_at: string | null }
  storage_ready: boolean
  legal_text: string
  legal_text_version: string
  can_confirm: boolean
  retention_days: number
  storage: StorageInfo | null
  pending_uploads: number
}

interface Rec {
  id: string
  extension: string
  mode: string
  direction: string | null
  peer_number: string | null
  peer_name: string | null
  started_at: string
  duration: number | null
  stored: boolean
  on_pbx: boolean
  local_deleted_at: string | null
  last_error: string | null
}

const CONFIRM_ITEMS = [
  'Alle Gesprächsteilnehmenden werden vor Beginn der Aufnahme informiert.',
  'Die Mitarbeitenden sind schriftlich über die Aufnahme informiert.',
  'Die Aufnahmen dienen einem rechtmässigen Zweck und nicht der Verhaltensüberwachung.',
  'Wir sind für Aufbewahrung, Zugriff, Auskunft und Löschung auf unserem Speicherort verantwortlich.',
  'Die it64 GmbH stellt nur die Technik bereit; die Verantwortung liegt beim Mandanten.',
]

const emptyForm = {
  storage_type: 'sftp', host: '', port: '22', username: '', url: '', endpoint: '',
  bucket: '', region: '', access_key: '', path: '', secret: '',
}

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString('de-CH') : '–')
const fmtDur = (d: number | null) => (d == null ? '–' : `${Math.floor(d / 60)}:${String(d % 60).padStart(2, '0')}`)
const dirLabel: Record<string, string> = { inbound: 'Eingehend', outbound: 'Ausgehend', internal: 'Intern' }

export default function CallRecording() {
  const confirm = useConfirm()
  const { user, selectedTenant } = useAuthStore()
  const needsTenant = user?.role === 'super_admin' && !selectedTenant
  const [status, setStatus] = useState<Status | null>(null)
  const [loading, setLoading] = useState(true)
  const [checks, setChecks] = useState<boolean[]>(CONFIRM_ITEMS.map(() => false))
  const [form, setForm] = useState({ ...emptyForm })
  const [busy, setBusy] = useState(false)
  const [testMsg, setTestMsg] = useState<{ ok: boolean; message: string } | null>(null)
  const [recs, setRecs] = useState<Rec[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [playing, setPlaying] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const load = async () => {
    if (needsTenant) { setLoading(false); return }
    try {
      const r = await callRecordingsApi.status()
      const s: Status = r.data
      setStatus(s)
      if (s.storage) {
        const c = s.storage.config || {}
        setForm({
          ...emptyForm,
          storage_type: s.storage.storage_type,
          host: String(c.host || ''), port: String(c.port || '22'), username: String(c.username || ''),
          url: String(c.url || ''), endpoint: String(c.endpoint || ''), bucket: String(c.bucket || ''),
          region: String(c.region || ''), access_key: String(c.access_key || ''), path: String(c.path || ''),
        })
      }
      const l = await callRecordingsApi.list({ page, page_size: 50 })
      setRecs(l.data.items || [])
      setTotal(l.data.total || 0)
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [selectedTenant?.id, page])
  useEffect(() => () => { audioRef.current?.pause() }, [])

  const errMsg = (e: unknown) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Fehler'

  const doConfirm = async () => {
    if (!status) return
    setBusy(true)
    try {
      await callRecordingsApi.confirmLegal(status.legal_text_version)
      toast.success('Rechtshinweis bestätigt')
      setChecks(CONFIRM_ITEMS.map(() => false))
      await load()
    } catch (e) { toast.error(errMsg(e)) } finally { setBusy(false) }
  }

  const doRevoke = async () => {
    if (!(await confirm({ title: 'Bestätigung widerrufen', message: 'Danach nimmt die Anlage für diesen Mandanten nichts mehr auf. Fortfahren?' }))) return
    try { await callRecordingsApi.revokeLegal(); toast.success('Widerrufen'); await load() } catch (e) { toast.error(errMsg(e)) }
  }

  const saveStorage = async () => {
    setBusy(true)
    setTestMsg(null)
    try {
      const data: Record<string, unknown> = { storage_type: form.storage_type, path: form.path }
      if (form.storage_type === 'sftp') Object.assign(data, { host: form.host, port: Number(form.port) || 22, username: form.username })
      if (form.storage_type === 'webdav') Object.assign(data, { url: form.url, username: form.username })
      if (form.storage_type === 's3') Object.assign(data, { endpoint: form.endpoint, bucket: form.bucket, region: form.region, access_key: form.access_key })
      if (form.secret) data.secret = form.secret
      await callRecordingsApi.saveStorage(data)
      setForm(f => ({ ...f, secret: '' }))
      toast.success('Speicherort gespeichert — bitte jetzt testen')
      await load()
    } catch (e) { toast.error(errMsg(e)) } finally { setBusy(false) }
  }

  const testStorage = async () => {
    setBusy(true)
    setTestMsg(null)
    try {
      const r = await callRecordingsApi.testStorage()
      setTestMsg({ ok: r.data.ok, message: r.data.message })
      await load()
    } catch (e) { setTestMsg({ ok: false, message: errMsg(e) }) } finally { setBusy(false) }
  }

  const removeStorage = async () => {
    if (!(await confirm({ title: 'Speicherort entfernen', message: 'Ohne Speicherort nimmt die Anlage nichts mehr auf. Bereits abgelegte Aufnahmen bleiben auf Ihrem Speicherort. Fortfahren?' }))) return
    try { await callRecordingsApi.deleteStorage(); setForm({ ...emptyForm }); toast.success('Entfernt'); await load() } catch (e) { toast.error(errMsg(e)) }
  }

  const play = async (rec: Rec) => {
    audioRef.current?.pause()
    if (playing === rec.id) { setPlaying(null); return }
    try {
      const r = await callRecordingsApi.audio(rec.id)
      const url = URL.createObjectURL(r.data as Blob)
      const a = new Audio(url)
      audioRef.current = a
      a.onended = () => { setPlaying(null); URL.revokeObjectURL(url) }
      setPlaying(rec.id)
      await a.play()
    } catch (e) { setPlaying(null); toast.error(errMsg(e)) }
  }

  if (needsTenant) return <Alert variant="info">Bitte oben einen Mandanten wählen.</Alert>
  if (loading || !status) return <div className="p-4"><Spinner animation="border" size="sm" /> Laden…</div>

  const st = status.storage
  const set = (k: keyof typeof emptyForm) => (e: React.ChangeEvent<{ value: string }>) =>
    setForm({ ...form, [k]: e.target.value })

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0"><i className="bi bi-record-circle me-2" />Gesprächsaufnahme</h2>
        {status.allowed
          ? <Badge bg="success" className="fs-6">Aufnahme freigegeben</Badge>
          : <Badge bg="secondary" className="fs-6">Aufnahme gesperrt</Badge>}
      </div>

      {!status.allowed && (
        <Alert variant="warning">
          Die Anlage nimmt für diesen Mandanten nichts auf, solange folgendes fehlt:
          <ul className="mb-0 mt-1">{status.reasons.map(r => <li key={r}>{r}</li>)}</ul>
        </Alert>
      )}

      {/* 1. Rechtshinweis */}
      <Card className="mb-3">
        <Card.Header><strong>1. Rechtshinweis (Schweiz)</strong> <small className="text-muted">Version {status.legal_text_version}</small></Card.Header>
        <Card.Body>
          <div className="border rounded p-3 mb-3 bg-light" style={{ whiteSpace: 'pre-wrap', maxHeight: 360, overflowY: 'auto', fontSize: '0.9rem' }}>
            {status.legal_text}
          </div>
          {status.consent.confirmed ? (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span><i className="bi bi-check-circle-fill text-success me-1" />
                Bestätigt von {status.consent.confirmed_by} am {fmtDate(status.consent.confirmed_at)}</span>
              <Button variant="outline-danger" size="sm" onClick={doRevoke}>Bestätigung widerrufen</Button>
            </div>
          ) : status.can_confirm ? (
            <>
              {CONFIRM_ITEMS.map((t, i) => (
                <Form.Check key={i} id={`rc-${i}`} className="mb-2" label={t} checked={checks[i]}
                  onChange={e => setChecks(checks.map((c, j) => (j === i ? e.target.checked : c)))} />
              ))}
              <Button className="mt-2" disabled={busy || !checks.every(Boolean)} onClick={doConfirm}>
                Hinweis gelesen und bestätigen
              </Button>
            </>
          ) : (
            <Alert variant="info" className="mb-0">
              Der Hinweis muss von einem Administrator dieses Mandanten selbst bestätigt werden
              (Anmeldung mit seinem eigenen Mandanten-Konto).
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* 2. Speicherort */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>2. Externer Speicherort</strong>
          {st && (st.verified_at && !st.last_error
            ? <Badge bg="success">Getestet {fmtDate(st.verified_at)}</Badge>
            : <Badge bg="warning" text="dark">Nicht getestet</Badge>)}
        </Card.Header>
        <Card.Body>
          <p className="text-muted small">
            Aufnahmen werden dort abgelegt und geprüft (Grösse und Prüfsumme). Erst danach — und
            frühestens {status.retention_days} Tage nach dem Gespräch — löscht die Telefonanlage ihre Kopie.
            Nicht geprüfte Aufnahmen werden nie gelöscht.
          </p>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Typ</Form.Label>
              <Form.Select value={form.storage_type} onChange={set('storage_type')}>
                <option value="sftp">SFTP (Schlüssel)</option>
                <option value="webdav">WebDAV / Nextcloud</option>
                <option value="s3">S3-kompatibel</option>
              </Form.Select>
            </Col>
            <Col md={8}>
              <Form.Label>Ordner auf dem Ziel</Form.Label>
              <Form.Control value={form.path} onChange={set('path')} placeholder="z. B. Telefonanlage/Aufnahmen" />
            </Col>
            {form.storage_type === 'sftp' && <>
              <Col md={6}><Form.Label>Server</Form.Label><Form.Control value={form.host} onChange={set('host')} placeholder="sftp.example.ch" /></Col>
              <Col md={2}><Form.Label>Port</Form.Label><Form.Control value={form.port} onChange={set('port')} /></Col>
              <Col md={4}><Form.Label>Benutzer</Form.Label><Form.Control value={form.username} onChange={set('username')} /></Col>
            </>}
            {form.storage_type === 'webdav' && <>
              <Col md={8}><Form.Label>WebDAV-Adresse (https)</Form.Label><Form.Control value={form.url} onChange={set('url')} placeholder="https://cloud.example.ch/remote.php/dav/files/benutzer" /></Col>
              <Col md={4}><Form.Label>Benutzer</Form.Label><Form.Control value={form.username} onChange={set('username')} /></Col>
              <Col md={6}><Form.Label>Passwort / App-Passwort</Form.Label>
                <Form.Control type="password" value={form.secret} onChange={set('secret')} placeholder={st?.has_secret ? '•••••• (unverändert)' : ''} autoComplete="new-password" /></Col>
            </>}
            {form.storage_type === 's3' && <>
              <Col md={6}><Form.Label>Endpoint (https)</Form.Label><Form.Control value={form.endpoint} onChange={set('endpoint')} placeholder="https://s3.example.ch" /></Col>
              <Col md={3}><Form.Label>Bucket</Form.Label><Form.Control value={form.bucket} onChange={set('bucket')} /></Col>
              <Col md={3}><Form.Label>Region</Form.Label><Form.Control value={form.region} onChange={set('region')} placeholder="us-east-1" /></Col>
              <Col md={6}><Form.Label>Access Key</Form.Label><Form.Control value={form.access_key} onChange={set('access_key')} /></Col>
              <Col md={6}><Form.Label>Secret Key</Form.Label>
                <Form.Control type="password" value={form.secret} onChange={set('secret')} placeholder={st?.has_secret ? '•••••• (unverändert)' : ''} autoComplete="new-password" /></Col>
            </>}
          </Row>

          {form.storage_type === 'sftp' && st?.storage_type === 'sftp' && st.sftp_public_key && (
            <div className="mt-3">
              <Form.Label>Öffentlicher Schlüssel der Telefonanlage — beim Ziel in <code>~/.ssh/authorized_keys</code> eintragen</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control as="textarea" rows={2} readOnly value={st.sftp_public_key} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
                <Button variant="outline-secondary" onClick={() => { navigator.clipboard?.writeText(st.sftp_public_key || ''); toast.info('Kopiert') }}>
                  <i className="bi bi-clipboard" /></Button>
              </div>
              {st.sftp_host_key_fingerprint && <small className="text-muted">Host-Schlüssel des Servers (fixiert): <code>{st.sftp_host_key_fingerprint}</code></small>}
            </div>
          )}

          <div className="d-flex gap-2 mt-3 flex-wrap">
            <Button onClick={saveStorage} disabled={busy}>Speichern</Button>
            <Button variant="outline-primary" onClick={testStorage} disabled={busy || !st}>
              {busy ? <Spinner size="sm" animation="border" /> : 'Verbindung testen'}</Button>
            {st && <Button variant="outline-danger" onClick={removeStorage} disabled={busy}>Entfernen</Button>}
          </div>
          {testMsg && <Alert className="mt-3 mb-0" variant={testMsg.ok ? 'success' : 'danger'}>{testMsg.message}</Alert>}
          {!testMsg && st?.last_error && <Alert className="mt-3 mb-0" variant="danger">Letzter Test: {st.last_error}</Alert>}
        </Card.Body>
      </Card>

      {/* 3. Aufnahmen */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>3. Aufnahmen</strong>
          {status.pending_uploads > 0 && <Badge bg="warning" text="dark">{status.pending_uploads} noch nicht abgelegt</Badge>}
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead><tr><th>Zeit</th><th>Nebenstelle</th><th>Richtung</th><th>Gegenstelle</th><th>Dauer</th><th>Art</th><th>Ablage</th><th /></tr></thead>
            <tbody>
              {recs.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">Keine Aufnahmen</td></tr>}
              {recs.map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.started_at)}</td>
                  <td>{r.extension}</td>
                  <td>{dirLabel[r.direction || ''] || '–'}</td>
                  <td>{r.peer_name ? `${r.peer_name} ` : ''}<span className="text-muted">{r.peer_number || ''}</span></td>
                  <td>{fmtDur(r.duration)}</td>
                  <td>{r.mode === 'manual' ? 'Knopf' : 'Automatisch'}</td>
                  <td>
                    {r.stored ? <Badge bg="success">abgelegt + geprüft</Badge>
                      : <Badge bg="warning" text="dark" title={r.last_error || ''}>ausstehend</Badge>}
                    {' '}{r.on_pbx ? <Badge bg="light" text="dark">auf Anlage</Badge> : <Badge bg="light" text="muted">Anlage gelöscht</Badge>}
                  </td>
                  <td><Button size="sm" variant="outline-primary" onClick={() => play(r)}>
                    <i className={`bi ${playing === r.id ? 'bi-stop-fill' : 'bi-play-fill'}`} /></Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        {total > 50 && (
          <Card.Footer className="d-flex justify-content-between">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Zurück</Button>
            <small className="text-muted">Seite {page} von {Math.ceil(total / 50)}</small>
            <Button size="sm" variant="outline-secondary" disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>Weiter</Button>
          </Card.Footer>
        )}
      </Card>
    </div>
  )
}
